import { useCallback, useEffect, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import fetchChatbotProfile from "./fetchChatbotProfile";
const chatbotProfileLogger = appLogger.withTag("ChatbotProfile");
const idleProfileState = { context: null, viewModel: { status: "idle" } };
function isSameRequestContext(previous, current) {
    if (previous === null || current === null) {
        return previous === current;
    }
    return previous.characterId === current.characterId && previous.language === current.language;
}
function buildInitialProfileState(context) {
    return context === null ? idleProfileState : { context, viewModel: { status: "loading" } };
}
/** Request-scoped profile loader: hosts pass language and readiness in, nothing is read from Redux. */
export default function useChatbotProfile({ characterId, language, isEnabled }) {
    const currentContext = isEnabled && characterId ? { characterId, language } : null;
    const [stateProfile, setStateProfile] = useState(() => buildInitialProfileState(currentContext));
    const [stateManualRetryCount, setStateManualRetryCount] = useState(0);
    const retry = useCallback(() => {
        setStateManualRetryCount((manualRetryCount) => manualRetryCount + 1);
    }, []);
    if (!isSameRequestContext(stateProfile.context, currentContext)) {
        // Render-phase reset so a stale view model is never committed.
        setStateProfile(buildInitialProfileState(currentContext));
    }
    useEffect(() => {
        if (!isEnabled || !characterId) {
            return undefined;
        }
        const context = { characterId, language };
        const abortController = new AbortController();
        setStateProfile(buildInitialProfileState(context));
        void fetchChatbotProfile({ characterId, language, signal: abortController.signal }).then((result) => {
            // Aborted by the effect cleanup; ignore.
            if (abortController.signal.aborted || result.status === "cancelled") {
                return;
            }
            if (result.status === "success") {
                setStateProfile({ context, viewModel: { status: "ready", character: result.character } });
                return;
            }
            if (result.status === "not-found") {
                setStateProfile({ context, viewModel: { status: "not-found" } });
                return;
            }
            chatbotProfileLogger.error("Chatbot profile request failed", {
                characterId,
                category: result.category,
                httpStatus: result.httpStatus,
            });
            setStateProfile({ context, viewModel: { status: "error", retry } });
        });
        return () => {
            abortController.abort();
        };
    }, [characterId, language, isEnabled, stateManualRetryCount, retry]);
    return stateProfile.viewModel;
}
